import { Audit } from "models/generateAuditSchemas";
import moment from "moment";
import { toDateString, toUnixEpochTime } from "utils/date-format";
import { getAuditName } from "components/audit-name/AuditName";
import { AuditNameProps } from "components/audit-name/definitions";
import { determineJurisdictionLevel } from "utils/jurisdictionUtils";
import { generateDynamicAttributesDataMap } from "utils/dynamicAttributesUtils";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import {
  getEntityCodeFromLongName,
  getEntityNameFromEntityCompositeKey,
} from "features/ui-content/utils";

export const toAuditDateString = (audit: Audit) => {
  audit.auditStartDateString = toDateString(audit.auditStartDate);
  audit.auditEndDateString = toDateString(audit.auditEndDate);
  audit.auditInitiationDateString = toDateString(audit.auditInitiationDate);
  audit.auditCloseDateString = toDateString(audit.auditCloseDate!);
  audit.statuteOfLimitationsString = toDateString(audit.statuteOfLimitations!);
  return audit;
};

export const extractYear = (date: number) => {
  return moment.utc(date * 1000).format("YYYY");
};

/**
 * Centralized error handling for audit operations
 * @param error - The error object from the API call
 * @param defaultMessage - The default message to show for non-duplicate errors
 * @returns The appropriate error message to display to the user
 */
export const getAuditErrorMessage = (
  error: any,
  defaultMessage: string,
): string => {
  const backendMessage = error?.data?.message || "";
  const isDuplicateError = backendMessage.includes("Duplicate audit found");

  return isDuplicateError ? "Error - duplicate audit exists." : defaultMessage;
};

/**
 * Helper function to find template value by label from team-specific templates
 * @param templateLabel - The template label to search for
 * @param teamTemplatesMap - Map of team names to their template options
 * @param teamName - The current team name
 * @returns The template value if found, undefined otherwise
 */
export const findTemplateValueByLabel = (
  templateLabel: string,
  teamTemplatesMap: Map<
    string,
    Array<{ label: string; value: string | undefined }>
  >,
  teamName: string,
): string | undefined => {
  return teamTemplatesMap
    .get(teamName)
    ?.find((template) => template.label === templateLabel)?.value;
};

export const processAuditData = (
  audit: any,
  {
    teamName,
    uiContent,
    auditCustomAttributes,
  }: { teamName: string; uiContent: any; auditCustomAttributes?: any },
) => {
  if (audit.entity) {
    audit.entity = getEntityNameFromEntityCompositeKey(audit.entity);
  }
  const auditNameProps: AuditNameProps = {
    country: audit.country,
    governmentEntity: audit.governmentEntity,
    localJurisdiction: audit.localJurisdiction,
    entity: audit.entity,
    taxType: audit.taxType,
    auditStartDateString: audit.auditStartDateString,
    auditEndDateString: audit.auditEndDateString,
    auditNameSuffix: audit.auditNameSuffix,
    entityCode: audit.entityCode,
  };
  audit.auditName = getAuditName(auditNameProps);
  audit.teamName = teamName;
  audit.jurisdictionLevel = determineJurisdictionLevel(
    audit.governmentEntity,
    audit.localJurisdiction,
  );
  audit.auditStartDate = toUnixEpochTime(audit.auditStartDateString);
  audit.auditEndDate = toUnixEpochTime(audit.auditEndDateString);
  audit.auditInitiationDate = toUnixEpochTime(audit.auditInitiationDateString);
  audit.auditCloseDate = toUnixEpochTime(audit.auditCloseDateString);
  audit.statuteOfLimitations = toUnixEpochTime(
    audit.statuteOfLimitationsString,
  );
  audit.statusActionOwner = "Amazon";
  audit.templateId = audit.template;
  audit.entityCode = getEntityCodeFromLongName(audit.entity, uiContent);

  if (auditCustomAttributes) {
    audit.customAttributesDataMap = generateDynamicAttributesDataMap(
      auditCustomAttributes,
      audit[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME],
    );
  }

  if (audit.pncEnabled) {
    audit.pncEnabled = true;
    audit.pncUsers = audit.pncUsers ?? [];
  }
  return audit;
};
